const JJ_CM_PER_INCH = 2.54;

function jjRound(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}

function jjBagArea(size, multiplier = 4) {
  const length = Math.max(0, Number(size.length) || 0);
  const width = Math.max(0, Number(size.width) || 0);
  const height = Math.max(0, Number(size.height) || 0);
  return (length + width + 2) * multiplier * (height + width + 4);
}

function jjFindTier(tiers, quantity) {
  return [...tiers]
    .sort((a, b) => Number(b.min) - Number(a.min))
    .find((tier) => quantity >= Number(tier.min)) || tiers[0];
}

function jjCalculateQuote(config, state, cnyPerUsd) {
  const quantity = Math.max(Number(config.minimumQuantity) || 1, Number(state.quantity) || 0);
  const tier = jjFindTier(config.quantityTiers || [], quantity);
  const productType = (config.productTypes || []).find((item) => item.id === state.productType);
  const material = (config.materials || []).find((item) => item.id === state.material);
  const leadTime = (config.leadTimes || []).find((item) => item.id === state.leadTime);
  const shippingMethod = (config.shipping || []).find((item) => item.id === state.shipping);
  const processes = (config.processes || []).filter((item) => state.processes.includes(item.id) && item.id !== 'none');
  const referenceSize = config.referenceSize || { length: 36, width: 26, height: 8 };
  const dimensions = state.dimensions;

  const referenceArea = Math.max(1, jjBagArea(referenceSize, 4));
  const actualArea = Math.max(1, jjBagArea(dimensions, 4));
  const sizeFactor = state.sizeMode === 'custom' ? actualArea / referenceArea : 1;
  const typeMultiplier = Number(productType?.multiplier) || 1;
  const tierUnitPrice = Number(tier?.unitPrice) || 0;
  const baseUnitPrice = tierUnitPrice * sizeFactor * typeMultiplier;
  const materialUnitFee = baseUnitPrice * (Number(material?.surchargeRate) || 0);
  const processUnitFee = processes.reduce((total, item) => total + (Number(item.unitFee) || 0), 0);
  const setupFee = processes.reduce((total, item) => total + (Number(item.setupFee) || 0), 0);
  const unitBeforeLead = baseUnitPrice + materialUnitFee + processUnitFee;
  const goodsBeforeLead = unitBeforeLead * quantity + setupFee;
  const leadMultiplier = Number(leadTime?.multiplier) || 1;
  const leadAdjustment = goodsBeforeLead * (leadMultiplier - 1);
  const productionTotal = goodsBeforeLead + leadAdjustment;

  const shippingArea = jjBagArea(dimensions, 2);
  const weightFactor = Number(config.formula?.paperWeightKgPerSquareMeter) || 0.35;
  const estimatedWeightKg = (shippingArea / 10000) * weightFactor * quantity;
  const shippingRate = Number(shippingMethod?.ratePerKg) || 0;
  const shippingCost = estimatedWeightKg * shippingRate;
  const totalCny = Math.max(0, productionTotal + shippingCost);
  const safeRate = Math.max(0.01, Number(cnyPerUsd) || 7.2);

  return {
    quantity,
    tier,
    dimensions,
    sizeFactor,
    tierUnitPrice,
    baseUnitPrice,
    materialUnitFee,
    processUnitFee,
    setupFee,
    unitBeforeLead,
    goodsBeforeLead,
    leadAdjustment,
    productionTotal,
    estimatedWeightKg,
    shippingCost,
    totalCny,
    totalUsd: totalCny / safeRate,
    unitCny: totalCny / quantity,
    unitUsd: totalCny / quantity / safeRate,
  };
}

function jjEscape(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function jjFormatMoney(value, currency) {
  const locale = currency === 'CNY' ? 'zh-CN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function jjFormatDimension(valueCm, unit) {
  const value = unit === 'in' ? Number(valueCm) / JJ_CM_PER_INCH : Number(valueCm);
  return Number.isInteger(value) ? String(value) : String(jjRound(value, 2));
}

const JJHTMLElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class JJProductConfigurator extends JJHTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const configNode = this.querySelector('[data-jj-config]');
    const app = this.querySelector('[data-jj-app]');
    if (!configNode || !app) return;

    try {
      this.config = JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      app.innerHTML = '<p class="jj-configurator__error">报价配置读取失败，请检查产品报价 JSON。</p>';
      console.error('Invalid JJ quote configuration', error);
      return;
    }

    this.app = app;
    this.cnyPerUsd = Math.max(0.01, Number(this.dataset.cnyPerUsd) || 7.2);
    this.formId = '';
    this.productForm = null;
    this.state = this.createInitialState();
    this.render();
    this.bindEvents();
    this.connectProductForm();
    this.updateAll();
  }

  createInitialState() {
    const firstSize = this.config.standardSizes?.[0] || this.config.referenceSize || { length: 36, width: 26, height: 8 };
    return {
      productType: this.config.productTypes?.[0]?.id || '',
      quantity: Number(this.config.minimumQuantity) || 10,
      sizeMode: 'standard',
      sizeId: firstSize.id || '',
      dimensions: {
        length: Number(firstSize.length) || 0,
        width: Number(firstSize.width) || 0,
        height: Number(firstSize.height) || 0,
      },
      unit: 'cm',
      material: this.config.materials?.[0]?.id || '',
      processes: this.config.processes?.some((item) => item.id === 'none') ? ['none'] : [],
      fileMode: 'later',
      leadTime: this.config.leadTimes?.[0]?.id || '',
      shipping: this.config.shipping?.[0]?.id || '',
      currency: this.dataset.defaultCurrency === 'CNY' ? 'CNY' : 'USD',
    };
  }

  render() {
    const quantityButtons = (this.config.quantityTiers || [])
      .map((tier) => `<button type="button" class="jj-choice" data-jj-quantity="${Number(tier.min)}">${Number(tier.min).toLocaleString()}</button>`)
      .join('');

    const productTypes = (this.config.productTypes || [])
      .map((item, index) => this.renderRadioCard('product-type', item.id, item.label, index === 0))
      .join('');

    const sizes = (this.config.standardSizes || [])
      .map((item, index) => this.renderRadioCard('size', item.id, item.label, index === 0))
      .join('');

    const materials = (this.config.materials || [])
      .map((item, index) => {
        const suffix = Number(item.surchargeRate) > 0 ? ` (+${jjRound(Number(item.surchargeRate) * 100, 0)}%)` : '';
        return this.renderRadioCard('material', item.id, `${item.label}${suffix}`, index === 0, `data-groups="${jjEscape((item.groups || []).join(','))}"`);
      })
      .join('');

    const processes = (this.config.processes || [])
      .map((item) => {
        const fees = [];
        if (Number(item.unitFee)) fees.push(`+¥${jjRound(item.unitFee)}/件`);
        if (Number(item.setupFee)) fees.push(`制版 ¥${jjRound(item.setupFee)}`);
        return `
          <label class="jj-option-card jj-option-card--check" data-process-card="${jjEscape(item.id)}">
            <input type="checkbox" name="jj-process" value="${jjEscape(item.id)}" ${item.id === 'none' ? 'checked' : ''}>
            <span class="jj-option-card__mark" aria-hidden="true"></span>
            <span>
              <strong>${jjEscape(item.label)}</strong>
              ${fees.length ? `<small>${jjEscape(fees.join(' · '))}</small>` : '<small>免费 / Free</small>'}
            </span>
          </label>`;
      })
      .join('');

    const leadTimes = (this.config.leadTimes || [])
      .map((item, index) => {
        const percent = jjRound((Number(item.multiplier) - 1) * 100, 0);
        const suffix = percent > 0 ? `+${percent}%` : percent < 0 ? `${percent}%` : '标准价格';
        return this.renderRadioCard('lead-time', item.id, `${item.label} · ${suffix}`, index === 0);
      })
      .join('');

    const shipping = (this.config.shipping || [])
      .map((item, index) => this.renderRadioCard('shipping', item.id, `${item.label} · ¥${jjRound(item.ratePerKg)}/kg`, index === 0))
      .join('');

    this.app.innerHTML = `
      <div class="jj-configurator__layout">
        <div class="jj-configurator__steps">
          ${this.renderStep(1, '产品类型 / Product Type', `<div class="jj-options jj-options--two">${productTypes}</div>`)}
          ${this.renderStep(2, '数量 / Quantity', `
            <div class="jj-quantity-presets">${quantityButtons}</div>
            <label class="jj-field jj-field--compact">
              <span>自定义数量 / Custom quantity</span>
              <input type="number" data-jj-field="quantity" min="${Number(this.config.minimumQuantity) || 10}" step="1" value="${this.state.quantity}">
            </label>`)}
          ${this.renderStep(3, '规格 / Size', `
            <div class="jj-inline-toggle" aria-label="尺寸单位">
              <button type="button" data-jj-unit="cm" class="is-active">cm</button>
              <button type="button" data-jj-unit="in">inch</button>
            </div>
            <div class="jj-options">${sizes}</div>
            <label class="jj-option-card jj-option-card--custom">
              <input type="radio" name="jj-size" value="custom">
              <span class="jj-option-card__mark" aria-hidden="true"></span>
              <span><strong>自定义尺寸 / Custom size</strong><small>按相对展开面积计算</small></span>
            </label>
            <div class="jj-dimensions" data-jj-custom-dimensions hidden>
              ${this.renderDimensionField('length', '长 / Length')}
              ${this.renderDimensionField('width', '宽 / Width')}
              ${this.renderDimensionField('height', '高 / Height')}
            </div>`)}
          ${this.renderStep(4, '材质 / Material', `<div class="jj-options jj-options--two">${materials}</div><p class="jj-help" data-jj-material-help></p>`)}
          ${this.renderStep(5, '工艺 / Finishing', `<p class="jj-help">可多选；“无特殊工艺”与其他工艺互斥。至少选择一项。</p><div class="jj-options jj-options--two">${processes}</div>`)}
          ${this.renderStep(6, '设计文件 / Artwork', `
            <div class="jj-options jj-options--two">
              ${this.renderRadioCard('file-mode', 'later', '稍后提供 / Upload Later', true)}
              ${this.renderRadioCard('file-mode', 'now', '立即上传 / Upload Now', false)}
            </div>
            <label class="jj-file" data-jj-file-wrap hidden>
              <span>上传文件 / Upload artwork</span>
              <input type="file" data-jj-file accept=".ai,.eps,.pdf,.svg,.jpg,.jpeg,.png,.tif,.tiff,.psd">
              <small>AI、EPS、PDF、SVG、JPG、PNG、TIF 或 PSD</small>
            </label>`)}
          ${this.renderStep(7, '交期与运输 / Lead Time & Shipping', `
            <p class="jj-subheading">生产交期</p>
            <div class="jj-options">${leadTimes}</div>
            <p class="jj-subheading">运输方式</p>
            <div class="jj-options">${shipping}</div>`)}
        </div>

        <aside class="jj-quote" aria-live="polite">
          <div class="jj-quote__topline">
            <div>
              <span>即时估价</span>
              <strong>Instant Quote</strong>
            </div>
            <div class="jj-inline-toggle" aria-label="报价币种">
              <button type="button" data-jj-currency="USD" class="${this.state.currency === 'USD' ? 'is-active' : ''}">USD</button>
              <button type="button" data-jj-currency="CNY" class="${this.state.currency === 'CNY' ? 'is-active' : ''}">CNY</button>
            </div>
          </div>
          <div class="jj-quote__total" data-jj-total>—</div>
          <div class="jj-quote__unit" data-jj-unit-price></div>
          <dl class="jj-quote__breakdown" data-jj-breakdown></dl>
          <div class="jj-quote__summary" data-jj-summary></div>
          <p class="jj-quote__notice">估价基于当前规则；最终生产价以图稿、材料和物流审核为准。</p>
          <div class="jj-quote__status" data-jj-status></div>
        </aside>
      </div>`;
  }

  renderStep(number, title, content) {
    return `<section class="jj-step" data-jj-step="${number}"><div class="jj-step__heading"><span>STEP ${number}</span><h3>${jjEscape(title)}</h3></div>${content}</section>`;
  }

  renderRadioCard(name, value, label, checked, extra = '') {
    return `<label class="jj-option-card" ${extra}><input type="radio" name="jj-${name}" value="${jjEscape(value)}" ${checked ? 'checked' : ''}><span class="jj-option-card__mark" aria-hidden="true"></span><span><strong>${jjEscape(label)}</strong></span></label>`;
  }

  renderDimensionField(key, label) {
    return `<label class="jj-field"><span>${jjEscape(label)} (<b data-jj-dimension-unit>cm</b>)</span><input type="number" data-jj-dimension="${key}" min="0.1" step="0.1" value="${jjFormatDimension(this.state.dimensions[key], this.state.unit)}"></label>`;
  }

  bindEvents() {
    this.app.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('[data-jj-quantity]');
      if (quantityButton) {
        this.state.quantity = Number(quantityButton.dataset.jjQuantity);
        this.app.querySelector('[data-jj-field="quantity"]').value = String(this.state.quantity);
        this.updateAll();
        return;
      }

      const unitButton = event.target.closest('[data-jj-unit]');
      if (unitButton) {
        this.state.unit = unitButton.dataset.jjUnit === 'in' ? 'in' : 'cm';
        this.updateUnitControls();
        return;
      }

      const currencyButton = event.target.closest('[data-jj-currency]');
      if (currencyButton) {
        this.state.currency = currencyButton.dataset.jjCurrency === 'CNY' ? 'CNY' : 'USD';
        this.updateCurrencyControls();
        this.updateQuote();
      }
    });

    this.app.addEventListener('input', (event) => {
      const target = event.target;
      if (target.matches('[data-jj-field="quantity"]')) {
        this.state.quantity = Math.max(0, Math.floor(Number(target.value) || 0));
        this.updateAll();
      }
      if (target.matches('[data-jj-dimension]')) {
        const key = target.dataset.jjDimension;
        const value = Math.max(0, Number(target.value) || 0);
        this.state.dimensions[key] = this.state.unit === 'in' ? value * JJ_CM_PER_INCH : value;
        this.updateAll();
      }
    });

    this.app.addEventListener('change', (event) => {
      const target = event.target;
      if (target.name === 'jj-product-type') {
        this.state.productType = target.value;
        this.updateMaterialAvailability();
      } else if (target.name === 'jj-size') {
        this.handleSizeChange(target.value);
      } else if (target.name === 'jj-material') {
        this.state.material = target.value;
      } else if (target.name === 'jj-process') {
        this.handleProcessChange(target);
      } else if (target.name === 'jj-file-mode') {
        this.state.fileMode = target.value;
        this.updateFileControl();
      } else if (target.name === 'jj-lead-time') {
        this.state.leadTime = target.value;
      } else if (target.name === 'jj-shipping') {
        this.state.shipping = target.value;
      }
      this.updateAll();
    });
  }

  connectProductForm() {
    if (this.dataset.syncCartProperties !== 'true') return;
    const productId = String(this.dataset.productId || '');
    const component = Array.from(document.querySelectorAll('product-form-component')).find(
      (item) => String(item.dataset.productId || '') === productId
    );
    this.productForm = component?.querySelector('form[data-type="add-to-cart-form"]') || null;
    if (!this.productForm) return;

    this.formId = this.productForm.id;
    this.ensurePropertyInputs();
    this.productForm.addEventListener(
      'submit',
      (event) => {
        const validation = this.validate();
        if (validation.valid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showStatus(validation.message, true);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      true
    );
  }

  ensurePropertyInputs() {
    const properties = [
      ['产品类型', 'productType'],
      ['数量', 'quantity'],
      ['规格', 'size'],
      ['材质', 'material'],
      ['工艺', 'processes'],
      ['文件方式', 'fileMode'],
      ['图稿文件名', 'fileName'],
      ['交期', 'leadTime'],
      ['运输方式', 'shipping'],
      ['估价 CNY', 'totalCny'],
      ['估价 USD', 'totalUsd'],
      ['_报价版本', 'version'],
      ['_报价数据', 'payload'],
    ];
    for (const [label, key] of properties) {
      let input = this.productForm.querySelector(`[data-jj-property="${key}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${label}]`;
        input.dataset.jjProperty = key;
        this.productForm.appendChild(input);
      }
    }

    const fileInput = this.app.querySelector('[data-jj-file]');
    if (fileInput && this.formId) {
      fileInput.name = 'properties[设计文件]';
      fileInput.setAttribute('form', this.formId);
    }
  }

  handleSizeChange(value) {
    if (value === 'custom') {
      this.state.sizeMode = 'custom';
    } else {
      const size = this.config.standardSizes.find((item) => item.id === value);
      if (size) {
        this.state.sizeMode = 'standard';
        this.state.sizeId = value;
        this.state.dimensions = {
          length: Number(size.length),
          width: Number(size.width),
          height: Number(size.height),
        };
      }
    }
    this.updateCustomSizeVisibility();
    this.updateUnitControls();
  }

  handleProcessChange(target) {
    const checked = Array.from(this.app.querySelectorAll('input[name="jj-process"]:checked'));
    const selectedItem = this.config.processes.find((item) => item.id === target.value);
    if (target.checked && selectedItem) {
      const conflicts = selectedItem.conflicts || [];
      for (const input of checked) {
        if (input === target) continue;
        if (conflicts.includes('*') || conflicts.includes(input.value)) input.checked = false;
      }
      if (target.value !== 'none') {
        const none = this.app.querySelector('input[name="jj-process"][value="none"]');
        if (none) none.checked = false;
      }
    }

    let selected = Array.from(this.app.querySelectorAll('input[name="jj-process"]:checked')).map((input) => input.value);
    if (!selected.length) {
      const none = this.app.querySelector('input[name="jj-process"][value="none"]');
      if (none) {
        none.checked = true;
        selected = ['none'];
      }
    }
    this.state.processes = selected;
  }

  updateMaterialAvailability() {
    const productType = this.config.productTypes.find((item) => item.id === this.state.productType);
    const group = productType?.group || '';
    let currentAvailable = false;
    for (const input of this.app.querySelectorAll('input[name="jj-material"]')) {
      const card = input.closest('.jj-option-card');
      const groups = (card?.dataset.groups || '').split(',').filter(Boolean);
      const available = !groups.length || groups.includes(group);
      input.disabled = !available;
      card?.classList.toggle('is-disabled', !available);
      if (input.checked && available) currentAvailable = true;
    }
    if (!currentAvailable) {
      const fallback = Array.from(this.app.querySelectorAll('input[name="jj-material"]')).find((input) => !input.disabled);
      if (fallback) {
        fallback.checked = true;
        this.state.material = fallback.value;
      }
    }
    const help = this.app.querySelector('[data-jj-material-help]');
    if (help) help.textContent = group === 'paper' ? '纸袋支持标准、压纹、书写及洒金纸。' : '当前产品类型使用标准材质；纸张类加价选项已自动禁用。';
  }

  updateCustomSizeVisibility() {
    const custom = this.app.querySelector('[data-jj-custom-dimensions]');
    if (custom) custom.hidden = this.state.sizeMode !== 'custom';
  }

  updateUnitControls() {
    for (const button of this.app.querySelectorAll('[data-jj-unit]')) {
      button.classList.toggle('is-active', button.dataset.jjUnit === this.state.unit);
    }
    for (const label of this.app.querySelectorAll('[data-jj-dimension-unit]')) label.textContent = this.state.unit;
    for (const input of this.app.querySelectorAll('[data-jj-dimension]')) {
      input.value = jjFormatDimension(this.state.dimensions[input.dataset.jjDimension], this.state.unit);
    }
  }

  updateCurrencyControls() {
    for (const button of this.app.querySelectorAll('[data-jj-currency]')) {
      button.classList.toggle('is-active', button.dataset.jjCurrency === this.state.currency);
    }
  }

  updateFileControl() {
    const wrap = this.app.querySelector('[data-jj-file-wrap]');
    const input = this.app.querySelector('[data-jj-file]');
    if (wrap) wrap.hidden = this.state.fileMode !== 'now';
    if (input) {
      input.disabled = this.state.fileMode !== 'now';
      input.required = this.state.fileMode === 'now';
    }
  }

  updateAll() {
    this.updateMaterialAvailability();
    this.updateCustomSizeVisibility();
    this.updateFileControl();
    this.updateQuote();
    this.syncProductForm();
  }

  updateQuote() {
    const quote = jjCalculateQuote(this.config, this.state, this.cnyPerUsd);
    this.quote = quote;
    const currency = this.state.currency;
    const total = currency === 'CNY' ? quote.totalCny : quote.totalUsd;
    const unit = currency === 'CNY' ? quote.unitCny : quote.unitUsd;
    const convert = (value) => (currency === 'CNY' ? value : value / this.cnyPerUsd);

    this.app.querySelector('[data-jj-total]').textContent = jjFormatMoney(total, currency);
    this.app.querySelector('[data-jj-unit-price]').textContent = `${jjFormatMoney(unit, currency)} / 件 · ${quote.quantity.toLocaleString()} 件`;
    this.app.querySelector('[data-jj-breakdown]').innerHTML = `
      ${this.breakdownRow('数量阶梯基础单价', jjFormatMoney(convert(quote.tierUnitPrice), currency))}
      ${this.breakdownRow('规格系数', `${jjRound(quote.sizeFactor, 3)}×`)}
      ${this.breakdownRow('材质加价/件', jjFormatMoney(convert(quote.materialUnitFee), currency))}
      ${this.breakdownRow('工艺加价/件', jjFormatMoney(convert(quote.processUnitFee), currency))}
      ${this.breakdownRow('制版费', jjFormatMoney(convert(quote.setupFee), currency))}
      ${this.breakdownRow('交期调整', `${quote.leadAdjustment >= 0 ? '+' : '−'}${jjFormatMoney(Math.abs(convert(quote.leadAdjustment)), currency)}`)}
      ${this.breakdownRow(`预估运费 · ${jjRound(quote.estimatedWeightKg, 2)}kg`, jjFormatMoney(convert(quote.shippingCost), currency))}`;

    const labels = this.getSelectedLabels();
    this.app.querySelector('[data-jj-summary]').innerHTML = `
      <p><span>类型</span><strong>${jjEscape(labels.productType)}</strong></p>
      <p><span>规格</span><strong>${jjEscape(labels.size)}</strong></p>
      <p><span>材质</span><strong>${jjEscape(labels.material)}</strong></p>
      <p><span>工艺</span><strong>${jjEscape(labels.processes)}</strong></p>
      <p><span>交期</span><strong>${jjEscape(labels.leadTime)}</strong></p>
      <p><span>运输</span><strong>${jjEscape(labels.shipping)}</strong></p>`;

    const validation = this.validate();
    this.showStatus(validation.valid ? '配置完整，可提交询价或写入购物车。' : validation.message, !validation.valid);
    this.updateContactBody(labels, quote);
  }

  breakdownRow(label, value) {
    return `<div><dt>${jjEscape(label)}</dt><dd>${jjEscape(value)}</dd></div>`;
  }

  getSelectedLabels() {
    const find = (items, id) => items?.find((item) => item.id === id)?.label || '';
    const size = this.state.sizeMode === 'custom'
      ? `${jjFormatDimension(this.state.dimensions.length, this.state.unit)} × ${jjFormatDimension(this.state.dimensions.width, this.state.unit)} × ${jjFormatDimension(this.state.dimensions.height, this.state.unit)} ${this.state.unit}`
      : find(this.config.standardSizes, this.state.sizeId);
    const processes = this.config.processes
      .filter((item) => this.state.processes.includes(item.id))
      .map((item) => item.label)
      .join(', ');
    return {
      productType: find(this.config.productTypes, this.state.productType),
      size,
      material: find(this.config.materials, this.state.material),
      processes,
      fileMode: this.state.fileMode === 'now' ? '立即上传 / Upload Now' : '稍后提供 / Upload Later',
      leadTime: find(this.config.leadTimes, this.state.leadTime),
      shipping: find(this.config.shipping, this.state.shipping),
    };
  }

  updateContactBody(labels, quote) {
    const field = this.querySelector('[data-jj-contact-body]');
    if (!field) return;
    const fileName = this.app.querySelector('[data-jj-file]')?.files?.[0]?.name || '';
    field.value = [
      `产品：${this.dataset.productTitle || ''}`,
      `产品类型：${labels.productType}`,
      `数量：${quote.quantity}`,
      `规格：${labels.size}`,
      `材质：${labels.material}`,
      `工艺：${labels.processes}`,
      `文件：${labels.fileMode}${fileName ? ` (${fileName})` : ''}`,
      `交期：${labels.leadTime}`,
      `运输：${labels.shipping}`,
      `预估重量：${jjRound(quote.estimatedWeightKg, 2)} kg`,
      `预估总价：¥${jjRound(quote.totalCny, 2)} CNY / $${jjRound(quote.totalUsd, 2)} USD`,
      `汇率：1 USD = ${this.cnyPerUsd} CNY`,
      `报价版本：${this.config.version || 'custom'}`,
    ].join('\n');
  }

  syncProductForm() {
    if (!this.productForm || !this.quote) return;
    const labels = this.getSelectedLabels();
    const fileName = this.app.querySelector('[data-jj-file]')?.files?.[0]?.name || '';
    const payload = {
      version: this.config.version || 'custom',
      state: this.state,
      quote: {
        totalCny: jjRound(this.quote.totalCny, 2),
        totalUsd: jjRound(this.quote.totalUsd, 2),
        exchangeRate: this.cnyPerUsd,
      },
    };
    const values = {
      productType: labels.productType,
      quantity: String(this.quote.quantity),
      size: labels.size,
      material: labels.material,
      processes: labels.processes,
      fileMode: labels.fileMode,
      fileName,
      leadTime: labels.leadTime,
      shipping: labels.shipping,
      totalCny: `¥${jjRound(this.quote.totalCny, 2)}`,
      totalUsd: `$${jjRound(this.quote.totalUsd, 2)}`,
      version: this.config.version || 'custom',
      payload: JSON.stringify(payload),
    };
    for (const [key, value] of Object.entries(values)) {
      const input = this.productForm.querySelector(`[data-jj-property="${key}"]`);
      if (input) input.value = value;
    }

    const quantityInput = this.productForm.querySelector('input[name="quantity"]');
    if (quantityInput && Number(quantityInput.value) !== this.quote.quantity) {
      const selector = quantityInput.closest('quantity-selector-component');
      if (typeof selector?.setValue === 'function') selector.setValue(this.quote.quantity);
      else {
        quantityInput.value = String(this.quote.quantity);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  validate() {
    const minimum = Number(this.config.minimumQuantity) || 1;
    if (this.state.quantity < minimum) return { valid: false, message: `最低起订量为 ${minimum} 件。` };
    if (!this.state.productType) return { valid: false, message: '请选择产品类型。' };
    if (!this.state.material) return { valid: false, message: '请选择材质。' };
    if (this.state.sizeMode === 'custom' && Object.values(this.state.dimensions).some((value) => Number(value) <= 0)) {
      return { valid: false, message: '请填写完整的自定义长、宽、高。' };
    }
    const minimumProcesses = Number(this.config.processRules?.minSelect) || 0;
    if (this.state.processes.length < minimumProcesses) return { valid: false, message: `请至少选择 ${minimumProcesses} 项工艺。` };
    if (this.state.fileMode === 'now' && !this.app.querySelector('[data-jj-file]')?.files?.length) {
      return { valid: false, message: '请选择需要上传的设计文件，或选择稍后提供。' };
    }
    if (!this.state.leadTime) return { valid: false, message: '请选择交期。' };
    if (!this.state.shipping) return { valid: false, message: '请选择运输方式。' };
    return { valid: true, message: '' };
  }

  showStatus(message, isError = false) {
    const status = this.app.querySelector('[data-jj-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  }
}

globalThis.JJQuoteCalculator = { calculateQuote: jjCalculateQuote, bagArea: jjBagArea };

if (typeof customElements !== 'undefined' && !customElements.get('jj-product-configurator')) {
  customElements.define('jj-product-configurator', JJProductConfigurator);
}
